import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { NotificationsListener } from '@/app/platform/platform-core/platform-notifications/listeners/notifications-listener';
import { Announcement } from '@/app/platform/platform-data/models/announcement';
import { Observable } from 'rxjs';
import { BootstrapColor } from '@/types/css-variables.types';
import { NotificationLevel } from '@/app/platform/platform-data/models/notification-level';
import { BooleanInput } from '@shared/decorators/boolean-input.decorator';
import { AnnouncementBannerVariant } from './p-announcement-banner.component';

// Lokale BannerVariant aliased naar het banner-component-type zodat ze
// nooit uit sync raken (variant-uitbreidingen moeten enkel op de banner
// gebeuren).
interface BannerStyle {
  variant: AnnouncementBannerVariant;
  icon: string;
}

// FA-glyphs met ingebakken vorm én cutout: de banner toont nu géén eigen
// kleurvlak achter het icoon, maar laat de FA-glyph zelf de gekleurde
// vorm tekenen met de letter/teken als transparante cutout. De dark-banner
// bg toont door de cutout heen.
const bannerStyleByLevel: { [key in NotificationLevel]: BannerStyle } = {
  [NotificationLevel.Unknown]: { variant: 'info', icon: 'info-circle' },
  [NotificationLevel.Info]: { variant: 'info', icon: 'info-circle' },
  [NotificationLevel.Success]: { variant: 'success', icon: 'check-circle' },
  [NotificationLevel.Warning]: { variant: 'warning', icon: 'exclamation-triangle' },
  [NotificationLevel.Danger]: { variant: 'danger', icon: 'times-circle' }
};

@Component({
    selector: 'p-announcement',
    templateUrl: './p-announcement.component.html',
    styleUrls: ['./p-announcement.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PAnnouncementComponent {
  @Input('key') set keyInput(v: string) {
    this.announcement$ = this.listener.announcementByKey$(v);
  }

  /**
   * Schakelt de sluit-knop in/uit. Default `true`. Voor kritieke
   * (persistente) banners kan de parent dit op `false` zetten.
   * Het component verbergt zich visueel zelf bij sluiten en emit `closed`.
   */
  @BooleanInput() @Input() closable = true;

  @Output() readonly closed = new EventEmitter<void>();

  constructor(readonly listener: NotificationsListener, private readonly cdr: ChangeDetectorRef) {}

  announcement$?: Observable<Announcement>;
  color$: Observable<BootstrapColor>;

  protected styleFor(level: NotificationLevel): BannerStyle {
    return bannerStyleByLevel[level] ?? bannerStyleByLevel[NotificationLevel.Info];
  }

  protected onBannerClosed(): void {
    this.closed.emit();
    this.cdr.markForCheck();
  }
}

